import React, { useState, useEffect } from 'react';
import { ServiceKey } from '../data/serviceDetails';
import { Award, Share2, Megaphone, Globe, Search, Zap, Server } from 'lucide-react';

const brandingImg = '/src/assets/images/real_xweba_branding_service_1793794659233.jpg';
const socialImg = '/src/assets/images/real_xweba_social_service_1793794661372.jpg';
const adsImg = '/src/assets/images/real_xweba_ads_service_1793794664169.jpg';
const websitesImg = '/src/assets/images/real_xweba_websites_service_1793794668207.jpg';
const searchImg = '/src/assets/images/real_xweba_geo_service_1793794671323.jpg';
const automationImg = '/src/assets/images/real_xweba_automation_service_1793794672915.jpg';
const hostingImg = '/src/assets/images/real_xweba_hosting_service_1793794681401.jpg';
const servicesHeroCollabImg = '/src/assets/images/real_xweba_studio_team_1793794677785.jpg';
const officeCustomerCareImg = '/src/assets/images/xweba_office_customer_care_bg_1793403437018.jpg';

export const XWEBA_OFFICE_CUSTOMER_CARE_IMAGE = officeCustomerCareImg;
export const SERVICES_HERO_COLLABORATION_IMAGE = servicesHeroCollabImg;

export interface ServiceVisualMeta {
  src: string;
  secondarySrc: string;
  alt: string;
  secondaryAlt: string;
  caption: string;
  secondaryCaption: string;
  pillarLabel: string;
  icon: React.ComponentType<{ className?: string }>;
}

export const SERVICE_IMAGE_MAP: Record<ServiceKey, ServiceVisualMeta> = {
  branding: {
    src: brandingImg,
    secondarySrc: servicesHeroCollabImg,
    alt: 'Black African creative directors wearing official navy and white xweba. branded hoodies, polo shirts, and t-shirts in the xweba. design studio reviewing corporate identity guidelines and Pantone swatches',
    secondaryAlt: 'Xweba studio team of Black African brand strategists and engineers in official xweba. branded hoodies, polos, and t-shirts collaborating around a creative table',
    caption: '20+ Years Hands-On Brand Identity & Visual Design Systems',
    secondaryCaption: 'Collaborative Brand Strategy & Visual Architecture Studio',
    pillarLabel: '01 · Branding Consultant',
    icon: Award,
  },
  social: {
    src: socialImg,
    secondarySrc: servicesHeroCollabImg,
    alt: 'Black African and Spanish social media strategists wearing official navy and white xweba. branded zip-up hoodies, polo shirts, and t-shirts reviewing omnichannel content calendars',
    secondaryAlt: 'Xweba creative team in official xweba. branded apparel planning multi-platform social campaigns',
    caption: 'Omnichannel Social Media Strategy & Executive Authority',
    secondaryCaption: 'Content Pillar Direction & Audience Growth Analytics',
    pillarLabel: '02 · Social Media Consulting',
    icon: Share2,
  },
  ads: {
    src: adsImg,
    secondarySrc: brandingImg,
    alt: 'Black African performance marketers and Spanish ad specialist wearing official navy and white xweba. branded fleeces, t-shirts, and polos analyzing 4.5x ROAS campaign dashboards',
    secondaryAlt: 'Black African xweba. creative directors in official branded apparel reviewing high-converting ad creative systems',
    caption: 'Algorithmic Paid Media & High-ROAS Smart Advertising',
    secondaryCaption: 'Multi-Channel Attribution & Creative Conversion Testing',
    pillarLabel: '03 · Digital / Smart Ads',
    icon: Megaphone,
  },
  websites: {
    src: websitesImg,
    secondarySrc: searchImg,
    alt: 'Black African full-stack web architects wearing official navy and white xweba. branded hoodies, polo shirts, and t-shirts building high-speed Pro WordPress and AI-cited websites',
    secondaryAlt: 'Black African GEO engineers in official white and navy xweba. hoodies, button-down shirts, and polos verifying Schema.org entity graphs',
    caption: 'Pro WordPress & Custom Websites Engineered for AI Citation',
    secondaryCaption: 'Sub-Second Core Web Vitals & Semantic Entity Architecture',
    pillarLabel: '04 · Pro WordPress & AI Web',
    icon: Globe,
  },
  search: {
    src: searchImg,
    secondarySrc: websitesImg,
    alt: 'Black African AI search engineers wearing official white and navy xweba. hoodies, button-down shirts, and polo shirts examining Generative Engine Optimization (GEO) knowledge graphs',
    secondaryAlt: 'Black African web architects in official xweba. branded apparel implementing structured data and llms.txt discovery files',
    caption: 'Generative Engine Optimization (GEO) & Knowledge Graph Authority',
    secondaryCaption: 'Verified Schema.org Graphs & AI Engine Citation Engineering',
    pillarLabel: '05 · AI Search Visibility',
    icon: Search,
  },
  automation: {
    src: automationImg,
    secondarySrc: servicesHeroCollabImg,
    alt: 'Black African CRM solutions architects wearing official navy and white xweba. zip-up jackets, polo shirts, and sweatshirts reviewing 24/7 AI lead qualification and WhatsApp routing workflows',
    secondaryAlt: 'Black African xweba. engineers in assorted official xweba. branded hoodies, polos, and tees mapping end-to-end client acquisition pipelines',
    caption: '24/7 AI Lead Qualification, CRM Sync & <60s WhatsApp Routing',
    secondaryCaption: 'Autonomous Pipeline Engineering & Instant Lead Dispatch',
    pillarLabel: '06 · AI CRM Automation',
    icon: Zap,
  },
  hosting: {
    src: hostingImg,
    secondarySrc: websitesImg,
    alt: 'Black African cloud infrastructure engineers wearing official navy and white xweba. technical jackets, t-shirts, and hoodies monitoring global CDN routing and 99.9% uptime telemetry',
    secondaryAlt: 'Black African web engineers in official xweba. branded apparel optimizing cloud web performance and edge caching',
    caption: 'Official Hostinger Cloud Partner Provisioning & 99.9% Uptime SLA',
    secondaryCaption: 'Global Edge CDN, NVMe LiteSpeed & Enterprise Security',
    pillarLabel: '07 · Managed Cloud Hosting',
    icon: Server,
  },
};

const ALL_SERVICE_IMAGE_URLS = [
  servicesHeroCollabImg,
  brandingImg,
  socialImg,
  adsImg,
  websitesImg,
  searchImg,
  automationImg,
  hostingImg,
];

let imagesPrewarmed = false;
export function prewarmServiceImages(): void {
  if (imagesPrewarmed || typeof window === 'undefined') return;
  imagesPrewarmed = true;
  const schedule =
    typeof (window as any).requestIdleCallback === 'function'
      ? (window as any).requestIdleCallback
      : (cb: () => void) => setTimeout(cb, 150);

  schedule(() => {
    ALL_SERVICE_IMAGE_URLS.forEach((url) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    });
  });
}

export interface BrandedServiceImageProps {
  serviceKey?: ServiceKey;
  variant?: 'primary' | 'secondary';
  customSrc?: string;
  customTag?: string;
  customCaption?: string;
  title?: string;
  subtitle?: string;
  badgeText?: string;
  metricBadge?: string;
  aspectClass?: string;
  compact?: boolean;
  priority?: boolean;
  className?: string;
}

export const BrandedServiceImage: React.FC<BrandedServiceImageProps> = ({
  serviceKey = 'branding',
  variant = 'primary',
  customSrc,
  customTag,
  customCaption,
  title,
  subtitle,
  badgeText,
  metricBadge,
  aspectClass,
  compact = false,
  priority = false,
  className = '',
}) => {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const meta = SERVICE_IMAGE_MAP[serviceKey] || SERVICE_IMAGE_MAP.branding;
  const Icon = meta.icon;

  const isSecondary = variant === 'secondary';
  const displaySrc = customSrc || (isSecondary ? meta.secondarySrc : meta.src);

  useEffect(() => {
    setImgError(false);
    prewarmServiceImages();
  }, [displaySrc]);

  const displayAlt = title
    ? `${title}${subtitle ? ` - ${subtitle}` : ''}`
    : isSecondary
    ? meta.secondaryAlt
    : meta.alt;
  const displayPillarLabel = customTag || meta.pillarLabel;
  const displayBadge = metricBadge || badgeText;
  const displayCaption = customCaption || title || (isSecondary ? meta.secondaryCaption : meta.caption);
  const resolvedAspect = aspectClass || (compact ? 'aspect-16/10' : 'aspect-16/9');

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-slate-200/80 bg-[#071E38] group/img ${resolvedAspect} ${className}`}
    >
      {/* Low-overhead instant gradient backdrop while progressive JPEG streams in */}
      {!imgLoaded && !imgError && (
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-br from-[#0B2D52] via-[#103864] to-[#071E38] animate-pulse"
        />
      )}

      {!imgError ? (
        <img
          src={displaySrc}
          alt={displayAlt}
          width={compact ? 640 : 1120}
          height={compact ? 400 : 630}
          referrerPolicy="no-referrer"
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : 'auto'}
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgError(true)}
          className={`w-full h-full object-cover object-center transition-all duration-300 group-hover/img:scale-105 ${
            imgLoaded ? 'opacity-100' : 'opacity-95'
          }`}
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center bg-gradient-to-br from-[#0B2D52] via-[#071E38] to-slate-900">
          <div className="w-12 h-12 rounded-2xl bg-[#F26522]/20 border border-[#F26522]/40 flex items-center justify-center text-[#F26522] mb-3">
            <Icon className="w-6 h-6" />
          </div>
          <p className="text-xs font-bold text-white">{displayPillarLabel}</p>
          <p className="text-[11px] text-slate-300 mt-1 max-w-xs">{displayCaption}</p>
        </div>
      )}

      {/* Measured contrast scrim for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#071E38]/90 via-[#071E38]/25 to-transparent pointer-events-none" />

      {/* Top-left subtle Xweba wordmark + pillar label */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-2 pointer-events-none">
        <span className="px-2.5 py-1 rounded-md bg-[#071E38]/85 backdrop-blur-xs border border-white/15 text-[11px] font-mono font-bold text-white truncate">
          {displayPillarLabel}
        </span>
        {displayBadge && (
          <span className="px-2.5 py-1 rounded-md bg-[#F26522] text-white text-[11px] font-bold shadow-sm whitespace-nowrap shrink-0">
            {displayBadge}
          </span>
        )}
      </div>

      {/* Bottom caption */}
      <div className="absolute bottom-3 left-3 right-3 pointer-events-none">
        <p className="text-xs font-semibold text-white/95 leading-snug line-clamp-1">
          {displayCaption}
        </p>
        {subtitle && (
          <p className="text-[10px] text-slate-300 leading-tight line-clamp-1 mt-0.5">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
};

